// ENH-344: identify merchant-defined custom input fields on a widget.
//
// Custom attributes (e.g. "Store Location", "T-shirt Size") are captured by
// input components the merchant adds in the widget builder. Their submitted
// values live in the widget form `data`, keyed by the input's configured
// `name`. They are open ended, so we distinguish them from the built-in form
// fields with a denylist
// of the reserved built-in names rather than an allowlist.

// Widget config component types that render a merchant-editable input.
//
// These MUST match the export names in the `src/containers` barrel — that is
// the live registry: App.tsx does `import * as components from "./containers"`
// and passes it to Widget.tsx, which looks up `components[component]` against
// it. (`src/components/components.ts` mirrors the same names but is imported by
// nothing, so pinning against it proves nothing.) A name that is not an export
// silently collects nothing: the field never reaches getCustomFieldNames, so
// its value is never forwarded with the share/capture and is lost for good.
// That is exactly what "TextAreaInput" did (ENH-383); the component is
// registered as "TextArea". `__tests__/inputComponentRegistry.ts` pins each of
// these against that barrel so the two cannot drift again.
//
// Both "TextArea" (ENH-383) and "Dropdown" (FBE-6645) belong here: they arrived
// on separate branches, and keeping only one side silently drops every answer
// given to the other component.
export const INPUT_COMPONENT_TYPES = [
  "TextInput",
  "TextArea",
  "Checkbox",
  "EmailTagInput",
  "Dropdown",
];

// Built-in field names handled explicitly by the widget/share pipeline. Any
// input whose configured name is NOT one of these is a merchant-defined custom
// field whose value ENH-344 needs to persist to the customer record.
const RESERVED_FIELD_NAMES = new Set<string>([
  "name",
  "email",
  "emails",
  "recipients",
  "message",
  "reminder",
  "sendReminder",
  "signup",
  "subscribe",
  "subscribeEmail",
  "subscribeSms",
  "smsConsent",
  "iKnowThisPerson",
  "caslConsent",
  "mobileNumber",
  "mobileNumberRegion",
  // NOT reserved, deliberately: "customer.firstName" / "customer.lastName".
  // ENH-388 reserved them, which broke the ADVOCATE path in sandbox. Advocate
  // widgets carry those inputs to the server ONLY through this harvest --
  // getReferral/emailShare do no stamping of their own, and a config that does
  // not declare firstName/lastName action args has no other route. Harvested,
  // the dotted names resolve into a nested `customer` object that is stored on
  // the share/referral attributes bag; reserved, the values never leave the
  // browser. The Friend path is unaffected either way, because trackEvent
  // stamps first/last at the top level of the payload independently.
  // ENH-344: birthday / zipCode are now standard "known fields"
  // captured directly (top-level payload -> standard customer fields), so they
  // must NOT also be harvested as generic custom attributes.
  "birthday",
  "zipCode",
]);

/**
 * Returns the configured `name`s of the merchant-defined custom input fields on
 * a widget, derived from its component config. Built-in fields are excluded so
 * only genuine custom attributes are forwarded with a share/capture action.
 */
export const getCustomFieldNames = (
  // tslint:disable-next-line:no-any
  config: { [index: string]: any } = {}
): string[] =>
  Object.values(config)
    .filter(
      (component) =>
        component &&
        INPUT_COMPONENT_TYPES.indexOf(component.component) !== -1 &&
        typeof component.name === "string" &&
        component.name !== "" &&
        !RESERVED_FIELD_NAMES.has(component.name)
    )
    .map((component) => component.name);
